import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { makeStyles } from "@material-ui/core/styles";

import { GraphqlErrorMessage } from "../../../../components/ErrorMessage";
import { Loading } from "../../../../components/LoadingFork";
import { T } from "../../constants";
import { cx, useUi } from "../../ui";
import ModalCard from "../../components/ModalCard";
import Toast from "../../components/Toast";
import { DEFAULT_SHIFT } from "../../shifts/constants";
import useRunnerShifts from "../../shifts/useRunnerShifts";
import { DEFAULT_BLACKOUT_PERIOD } from "../../blackouts/blackoutHelpers";
import useBlackoutPeriods from "../../blackouts/useBlackoutPeriods";
import BlackoutPeriodRow from "./BlackoutPeriodRow";
import PickupIntervalRow from "./PickupIntervalRow";
import { useInstallation } from "../../../../components/InstallationContext";
import { listUsersQuery } from "../../../Settings/Users/queries";
import {
  areGuestVehicleStatusAlertsEnabled,
  getConciergeConfiguration,
  getConciergeConfigurationRecord,
  getRunnerVehiclePhotoMode,
  getUnassignedPickupAlertSettings,
  isAnprCameraEnabled,
  VEHICLE_PHOTO_MODES,
} from "../../../../lib/conciergeFeature";

export const updateFeatureConfigurationMutation = gql`
  mutation updateVehiclePhotoFeatureConfiguration(
    $facilityId: ID!
    $mode: RunnerVehiclePhotoModeEnum!
  ) {
    updateRunnerVehiclePhotoMode(facilityId: $facilityId, mode: $mode) {
      id
      configuration
    }
  }
`;

export const updateConciergeSettingsMutation = gql`
  mutation updateConciergeSettingsFeatureConfiguration(
    $id: ID!
    $configuration: JSON
  ) {
    updateFeatureFacilityProviderConfiguration(
      id: $id
      configuration: $configuration
    ) {
      id
      configuration
    }
  }
`;

export const isValidAlertEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

export const alertChannelHint = (user) => {
  const phone = Boolean(user?.phone?.trim());
  const email = isValidAlertEmail(user?.email);

  if (phone && email) return "SMS/EMAIL";
  if (phone) return "SMS";
  if (email) return "EMAIL";
  return "ERROR/invalid contact";
};

const hasInstallationRole = (user, installationId) =>
  user?.isEnabled !== false &&
  user?.userRoles?.some((userRole) => userRole?.installationId === installationId);

const useStyles = makeStyles({
  tabs: { display: "flex", gap: 4, marginBottom: 14, borderBottom: `1px solid ${T.borderSoft}` },
  tab: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    borderBottom: "2px solid transparent",
    '&[aria-selected="true"]': { color: T.primary, borderBottomColor: T.primary, fontWeight: 600 },
  },
  subhead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 12,
    color: T.mutedFg,
    margin: "4px 0 6px",
  },
  link: {
    border: "none",
    background: "transparent",
    color: T.primary,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    padding: 0,
    "&:disabled": { color: T.subtleFg, cursor: "not-allowed" },
  },
  muted: { fontSize: 12, color: T.mutedFg, marginBottom: 8 },
  list: { listStyle: "none", margin: 0, padding: 0 },
  error: { marginBottom: 12 },
  small: { padding: "8px 12px", fontSize: 13 },
  choices: { display: "grid", gap: 10 },
  choice: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 8,
    padding: 14,
    cursor: "pointer",
    background: T.surface,
    '&[data-selected="true"]': {
      borderColor: T.primary,
      background: T.primaryTint,
    },
  },
  radio: { marginTop: 2, accentColor: T.primary },
  choiceTitle: { fontSize: 13, fontWeight: 600, color: T.fg },
  choiceDescription: { marginTop: 3, fontSize: 12, lineHeight: 1.45, color: T.mutedFg },
  photoActions: { display: "flex", justifyContent: "flex-end", marginTop: 16 },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.fg },
  setting: { marginBottom: 22 },
  settingTitle: { fontSize: 13, fontWeight: 600, color: T.fg, marginBottom: 5 },
  fieldRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  number: { width: 90 },
  userSelect: { width: "100%" },
  recipients: { marginTop: 10, display: "grid", gap: 6 },
  recipient: {
    display: "grid",
    gridTemplateColumns: "minmax(110px, 1fr) minmax(150px, 1.4fr) minmax(110px, 1fr) auto auto",
    gap: 10,
    alignItems: "center",
    padding: "9px 10px",
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 8,
    fontSize: 12,
    "@media (max-width: 650px)": { gridTemplateColumns: "1fr" },
  },
  recipientName: { fontWeight: 600, color: T.fg },
  recipientMeta: { color: T.mutedFg, overflowWrap: "anywhere" },
  hint: { fontWeight: 600, color: T.primary, whiteSpace: "nowrap" },
  invalidHint: { color: T.danger },
  remove: { border: 0, background: "transparent", color: T.primary, cursor: "pointer" },
});

const TABS = [
  { key: "capacity", label: "Pickup capacity" },
  { key: "blackouts", label: "Blackout periods" },
  { key: "photos", label: "Vehicle photos" },
  { key: "alerts", label: "ANPR & Pickup Alerts" },
];

const PHOTO_OPTIONS = [
  {
    value: VEHICLE_PHOTO_MODES.OPTIONAL,
    label: "Optional",
    description: "Runners can take vehicle photos or skip the photo step.",
  },
  {
    value: VEHICLE_PHOTO_MODES.REQUIRED,
    label: "Required",
    description: "Runners must open the photo step; the Skip button is hidden.",
  },
  {
    value: VEHICLE_PHOTO_MODES.DISABLED,
    label: "Disabled",
    description: "Runners continue without seeing the vehicle photo step.",
  },
];

export default function PropertySettingsModal({ installationId, timezone, onClose }) {
  const s = useStyles();
  const ui = useUi();
  const { facility, updateFacility } = useInstallation();
  const [tab, setTab] = useState(TABS[0].key);
  const [adding, setAdding] = useState(false);
  const [addingBlackout, setAddingBlackout] = useState(false);
  const {
    shifts,
    loading,
    error,
    mutationError,
    mutationInFlight,
    toast,
    create,
    update,
    remove,
  } = useRunnerShifts(installationId);
  const blackouts = useBlackoutPeriods(installationId);
  const configuredPhotoMode = getRunnerVehiclePhotoMode(facility);
  const configuredAnprEnabled = isAnprCameraEnabled(facility);
  const configuredGuestVehicleStatusAlertsEnabled =
    areGuestVehicleStatusAlertsEnabled(facility);
  const configuredAlert = getUnassignedPickupAlertSettings(facility);
  const [photoMode, setPhotoMode] = useState(configuredPhotoMode);
  const [anprEnabled, setAnprEnabled] = useState(configuredAnprEnabled);
  const [guestVehicleStatusAlertsEnabled, setGuestVehicleStatusAlertsEnabled] = useState(
    configuredGuestVehicleStatusAlertsEnabled,
  );
  const [alertMinutes, setAlertMinutes] = useState(String(configuredAlert.minutes));
  const [alertUserIds, setAlertUserIds] = useState(configuredAlert.userIds);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [photoToast, setPhotoToast] = useState(null);
  const [settingsToast, setSettingsToast] = useState(null);
  const [savePhotoMode, { loading: savingPhotos, error: photoMutationError }] =
    useMutation(updateFeatureConfigurationMutation);
  const [saveSettings, { loading: savingSettings, error: settingsMutationError }] =
    useMutation(updateConciergeSettingsMutation);
  const { data: usersData, loading: usersLoading, error: usersError } = useQuery(
    listUsersQuery,
    { fetchPolicy: "no-cache", skip: tab !== "alerts" },
  );

  const users = (usersData?.allUsers || [])
    .filter((user) => hasInstallationRole(user, installationId))
    .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
  const selectedUsers = alertUserIds
    .map((id) => users.find((user) => user.id === id))
    .filter(Boolean);
  const alertMinutesNumber = Number(alertMinutes);
  const alertValid =
    alertMinutes !== "" &&
    Number.isInteger(alertMinutesNumber) &&
    alertMinutesNumber >= 0;

  useEffect(() => setPhotoMode(configuredPhotoMode), [configuredPhotoMode]);
  useEffect(() => setAnprEnabled(configuredAnprEnabled), [configuredAnprEnabled]);
  useEffect(
    () => setGuestVehicleStatusAlertsEnabled(configuredGuestVehicleStatusAlertsEnabled),
    [configuredGuestVehicleStatusAlertsEnabled],
  );
  useEffect(() => {
    setAlertMinutes(String(configuredAlert.minutes));
    setAlertUserIds(configuredAlert.userIds);
  }, [facility]);

  const savePhotos = async () => {
    const record = getConciergeConfigurationRecord(facility);
    if (!record?.id) return;

    try {
      const { data } = await savePhotoMode({
        variables: { facilityId: facility.id, mode: photoMode.toUpperCase() },
      });
      const saved = data?.updateRunnerVehiclePhotoMode;

      if (saved) {
        updateFacility({
          featureFacilityProviderConfigurations:
            facility.featureFacilityProviderConfigurations.map((item) =>
              item.id === saved.id ? { ...item, ...saved } : item,
            ),
        });
        setPhotoToast("Vehicle photo settings saved.");
      }
    } catch {
      // useMutation exposes the failure through photoMutationError below.
    }
  };

  const saveAlerts = async () => {
    const record = getConciergeConfigurationRecord(facility);
    if (!record?.id || !alertValid) return;

    const configuration = {
      ...(getConciergeConfiguration(facility) || {}),
      anprEnabled,
      guestVehicleStatusAlertsEnabled,
      unassignedPickupAlertMinutes: alertMinutesNumber,
      unassignedPickupAlertUserIds: alertUserIds,
    };

    try {
      const { data } = await saveSettings({
        variables: { id: record.id, configuration },
      });
      const saved = data?.updateFeatureFacilityProviderConfiguration;
      if (!saved) return;

      updateFacility({
        featureFacilityProviderConfigurations:
          (facility.featureFacilityProviderConfigurations || []).map((item) =>
            item.id === saved.id ? { ...item, ...saved } : item,
          ),
      });
      setSettingsToast("ANPR and pickup alert settings saved.");
    } catch {
      // useMutation exposes the failure through settingsMutationError below.
    }
  };

  const addPeriod = async (shift) => {
    const saved = await create(shift);
    if (saved !== false) setAdding(false);
    return saved;
  };
  const addBlackout = async (period) => {
    const saved = await blackouts.create(period);
    if (saved !== false) setAddingBlackout(false);
    return saved;
  };

  return (
    <ModalCard
      title="Property settings"
      subtitle="Pickup hours, capacity, blackout periods, vehicle photos and alerts for this hotel."
      onClose={onClose}
      width={600}
      footerLeft={
        <button type="button" className={cx(ui.mutedBtn, s.small)} onClick={onClose}>
          Cancel
        </button>
      }
    >
      <div className={s.tabs} role="tablist" aria-label="Property settings">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            className={s.tab}
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "capacity" && (
        <>
          {mutationError && <GraphqlErrorMessage className={s.error} error={mutationError} />}

          <div className={s.subhead}>
            <span>Pickup hours</span>
            <button
              type="button"
              className={s.link}
              disabled={adding || !installationId}
              onClick={() => setAdding(true)}
            >
              + Add period
            </button>
          </div>

          {loading && <Loading />}
          {error && <GraphqlErrorMessage error={error} />}

          {!loading && !error && (
            <>
              {!shifts.length && !adding && (
                <div className={s.muted}>
                  No pickup hours set. Pickups are unlimited all day.
                </div>
              )}

              <ul className={s.list}>
                {shifts.map((shift) => (
                  <PickupIntervalRow
                    key={shift.id}
                    shift={shift}
                    disabled={mutationInFlight}
                    onSave={update}
                    onDelete={remove}
                  />
                ))}
                {adding && (
                  <PickupIntervalRow
                    shift={DEFAULT_SHIFT}
                    disabled={mutationInFlight}
                    onSave={addPeriod}
                    onCancel={() => setAdding(false)}
                  />
                )}
              </ul>

              <div className={s.muted}>
                Times are local to the hotel and a period whose end is at or before its start
                runs overnight. Parking and pickups are limited separately, e.g. park 3 and
                collect 5 in the same 15 minutes. The grace period is how long after the end a
                runner may stay clocked on.
              </div>
            </>
          )}
        </>
      )}

      {tab === "blackouts" && (
        <>
          {blackouts.mutationError && <GraphqlErrorMessage className={s.error} error={blackouts.mutationError} />}
          <div className={s.subhead}>
            <span>Blackout periods</span>
            <button
              type="button"
              className={s.link}
              disabled={addingBlackout || !installationId}
              onClick={() => setAddingBlackout(true)}
            >
              + Add period
            </button>
          </div>
          {blackouts.loading && <Loading />}
          {blackouts.error && <GraphqlErrorMessage error={blackouts.error} />}
          {!blackouts.loading && !blackouts.error && (
            <>
              {!blackouts.periods.length && !addingBlackout && (
                <div className={s.muted}>No blackout periods set.</div>
              )}
              <ul className={s.list}>
                {blackouts.periods.map((period) => (
                  <BlackoutPeriodRow
                    key={period.id}
                    period={period}
                    timezone={timezone}
                    disabled={blackouts.mutationInFlight}
                    onSave={blackouts.update}
                    onDelete={blackouts.remove}
                  />
                ))}
                {addingBlackout && (
                  <BlackoutPeriodRow
                    period={DEFAULT_BLACKOUT_PERIOD}
                    timezone={timezone}
                    disabled={blackouts.mutationInFlight}
                    onSave={addBlackout}
                    onCancel={() => setAddingBlackout(false)}
                  />
                )}
              </ul>
              <div className={s.muted}>
                Date bounds are stored as the start of each selected hotel-local day. Leave both
                dates blank to repeat the blackout every day. A daily period whose end is at or
                before its start runs overnight.
              </div>
            </>
          )}
        </>
      )}

      {tab === "photos" && (
        <>
          {photoMutationError && (
            <GraphqlErrorMessage className={s.error} error={photoMutationError} />
          )}
          <div className={s.subhead}>Runner vehicle photos</div>
          <div className={s.muted}>
            Applies to arrival condition, parking handover and return condition photos.
            With “optional” staff see Take photos and Skip photos and the choice is
            recorded with their identity and the time; the ordinary workflow is never
            blocked. Damage notes can be added without a photo. Photos are captured
            with the device camera and uploaded to secure storage.
          </div>
          <div className={s.choices} role="radiogroup" aria-label="Runner vehicle photos">
            {PHOTO_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={s.choice}
                data-selected={photoMode === option.value}
              >
                <input
                  className={s.radio}
                  type="radio"
                  name="runnerVehiclePhotosMode"
                  value={option.value}
                  checked={photoMode === option.value}
                  onChange={() => {
                    setPhotoMode(option.value);
                    setPhotoToast(null);
                  }}
                />
                <span>
                  <div className={s.choiceTitle}>{option.label}</div>
                  <div className={s.choiceDescription}>{option.description}</div>
                </span>
              </label>
            ))}
          </div>
          {!getConciergeConfigurationRecord(facility)?.id && (
            <div className={s.muted}>Concierge feature configuration is unavailable.</div>
          )}
          <div className={s.photoActions}>
            <button
              type="button"
              className={cx(ui.primaryBtn, s.small)}
              disabled={
                savingPhotos ||
                photoMode === configuredPhotoMode ||
                !getConciergeConfigurationRecord(facility)?.id
              }
              onClick={savePhotos}
            >
              {savingPhotos ? "Saving..." : "Save"}
            </button>
          </div>
        </>
      )}

      {tab === "alerts" && (
        <>
          {settingsMutationError && (
            <GraphqlErrorMessage className={s.error} error={settingsMutationError} />
          )}
          <div className={s.setting}>
            <div className={s.settingTitle}>ANPR camera enabled</div>
            <div className={s.muted}>
              When enabled, camera-detected vehicles appear in the Arrivals queue for staff to match or accept.
            </div>
            <label className={s.check}>
              <input
                type="checkbox"
                checked={anprEnabled}
                onChange={(event) => {
                  setAnprEnabled(event.target.checked);
                  setSettingsToast(null);
                }}
              />
              {anprEnabled
                ? "Yes · camera arrivals shown in the Arrivals queue"
                : "No · Arrivals queue hidden and manual arrival is available in Expected"}
            </label>
          </div>

          <div className={s.setting}>
            <div className={s.settingTitle}>Guest vehicle status alerts</div>
            <div className={s.muted}>
              Send SMS/email updates to guests when their vehicle is parked, picked up by a runner, and delivered.
            </div>
            <label className={s.check}>
              <input
                type="checkbox"
                aria-label="Guest vehicle status alerts"
                checked={guestVehicleStatusAlertsEnabled}
                onChange={(event) => {
                  setGuestVehicleStatusAlertsEnabled(event.target.checked);
                  setSettingsToast(null);
                }}
              />
              {guestVehicleStatusAlertsEnabled
                ? "Enabled · guests receive vehicle status updates"
                : "Disabled · guests do not receive vehicle status updates"}
            </label>
          </div>

          <div className={s.setting}>
            <div className={s.settingTitle}>Unassigned pickup alert</div>
            <div className={s.muted}>
              Notify the selected users when a pickup has remained unassigned for this many minutes.
            </div>
            <div className={s.fieldRow}>
              <input
                id="unassigned-pickup-alert-minutes"
                aria-label="Unassigned pickup alert minutes"
                type="number"
                min="0"
                className={cx(ui.field, s.number)}
                value={alertMinutes}
                onChange={(event) => {
                  setAlertMinutes(event.target.value);
                  setSettingsToast(null);
                }}
              />
              <span className={s.muted}>minutes</span>
            </div>
            <div className={s.muted}>Set to 0 to disable unassigned pickup alerts.</div>
          </div>

          <div className={s.setting}>
            <div className={s.settingTitle}>Users to notify</div>
            <div className={s.muted}>Choose an active user assigned to this installation to add them to the alert list.</div>
            {usersLoading && <Loading />}
            {usersError && <GraphqlErrorMessage error={usersError} />}
            {!usersLoading && !usersError && (
              <>
                <select
                  className={cx(ui.field, s.userSelect)}
                  aria-label="Users to notify"
                  value={selectedUserId}
                  onChange={(event) => {
                    const userId = event.target.value;
                    setSelectedUserId("");
                    if (!userId) return;
                    setAlertUserIds((ids) =>
                      ids.includes(userId) ? ids : [...ids, userId],
                    );
                    setSettingsToast(null);
                  }}
                >
                  <option value="">Select user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email || "Unnamed user"}
                    </option>
                  ))}
                </select>
                <div className={s.recipients}>
                  {selectedUsers.map((user) => {
                    const hint = alertChannelHint(user);
                    return (
                      <div key={user.id} className={s.recipient}>
                        <span className={s.recipientName}>{user.name || "Unnamed user"}</span>
                        <span className={s.recipientMeta}>{user.email || "No email"}</span>
                        <span className={s.recipientMeta}>{user.phone || "No phone"}</span>
                        <span className={cx(s.hint, hint.startsWith("ERROR") && s.invalidHint)}>{hint}</span>
                        <button
                          type="button"
                          className={s.remove}
                          onClick={() => setAlertUserIds((ids) => ids.filter((id) => id !== user.id))}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                  {!selectedUsers.length && <div className={s.muted}>No users selected.</div>}
                </div>
              </>
            )}
          </div>

          {!getConciergeConfigurationRecord(facility)?.id && (
            <div className={s.muted}>Concierge feature configuration is unavailable.</div>
          )}
          <div className={s.photoActions}>
            <button
              type="button"
              className={cx(ui.primaryBtn, s.small)}
              disabled={savingSettings || !alertValid || !getConciergeConfigurationRecord(facility)?.id}
              onClick={saveAlerts}
            >
              {savingSettings ? "Saving..." : "Save settings"}
            </button>
          </div>
        </>
      )}

      {(toast || blackouts.toast || photoToast || settingsToast) && (
        <Toast>{toast || blackouts.toast || photoToast || settingsToast}</Toast>
      )}
    </ModalCard>
  );
}
